/**
 * @fileoverview Labels and links for the automatic-drafting review UI
 * (MOR-1041). Non-component exports live here so the component files stay
 * fast-refresh friendly.
 */

import type { FunctionReturnType } from "convex/server";
import type { api } from "@cvx/_generated/api";

export type DraftSuggestion = FunctionReturnType<
  typeof api.timesheets.drafting.listPendingSuggestions
>[number];
export type DraftEvidence = DraftSuggestion["evidence"][number];
export type DraftRun = NonNullable<
  FunctionReturnType<typeof api.timesheets.drafting.latestRun>
>;
export type SourceCoverage = DraftRun["sourceCoverage"][number];

export const KIND_LABEL: Record<DraftEvidence["kind"], string> = {
  email_sent: "Email",
  mail_sent: "Email",
  job_note: "File note",
  task_activity: "Task",
  scheduled_event: "Scheduled",
  calendar_event: "Meeting",
  document_generated: "Document",
  document_uploaded: "Upload",
  document_edited: "Document edit",
  report_activity: "Report",
  data_entry_edited: "Data update",
  agent_chat: "Agent chat",
  mail_triaged: "Mail",
};

export const SOURCE_LABEL: Record<DraftEvidence["source"], string> = {
  morgan: "Morgan",
  "microsoft-calendar": "Outlook calendar",
  "microsoft-mail": "Outlook",
  "microsoft-documents": "Microsoft 365",
};

export const BASIS_LABEL: Record<DraftSuggestion["durationBasis"], string> = {
  measured: "Measured",
  scheduled: "Scheduled",
  estimated: "Estimated",
  unknown: "Duration unknown",
};

export const BASIS_HINT: Record<DraftSuggestion["durationBasis"], string> = {
  measured: "Taken from a clocked interval.",
  scheduled:
    "The booked calendar interval. A booking does not prove attendance — confirm or adjust before accepting.",
  estimated:
    "An estimate from the firm's drafting policy and the evidence. Edit it before accepting.",
  unknown: "The evidence shows the work happened, not how long it took.",
};

export const SOURCE_STATE_LABEL: Record<DraftEvidence["sourceState"], string> =
  {
    available: "",
    missing: "Source no longer available",
    restricted: "Access removed",
    unavailable: "Source disconnected",
  };

export const COVERAGE_SOURCE_LABEL: Record<SourceCoverage["source"], string> = {
  morgan: "Morgan",
  calendar: "Calendar",
  mail: "Sent mail",
  documents: "Documents",
};

export const COVERAGE_STATUS_LABEL: Record<SourceCoverage["status"], string> = {
  complete: "complete",
  incomplete: "incomplete",
  unavailable: "unavailable",
  disabled: "off",
  consent_required: "reconnect needed",
  not_connected: "not connected",
};

/** Badge colour per coverage status (see `Badge` colour system). */
export function coverageColor(
  status: SourceCoverage["status"],
): "green" | "amber" | "red" | "gray" {
  switch (status) {
    case "complete":
      return "green";
    case "incomplete":
      return "amber";
    case "unavailable":
    case "consent_required":
      return "red";
    case "disabled":
    case "not_connected":
      return "gray";
  }
}

/** The browser's IANA timezone, for the settings default. */
export function browserTimezone(): string {
  try {
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone || "Australia/Sydney"
    );
  } catch {
    return "Australia/Sydney";
  }
}

export function formatMinutes(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m} min`;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}
